import React from 'react';
import styled, { css } from 'styled-components';
import { Link as GatsbyLink } from 'gatsby';
import PropTypes from 'prop-types';
import { isExternalLink } from './Link';

const ButtonContainer = styled.div`
  display: flex;
  ${({ align }) => {
    switch (align) {
      case 'center':
        return css`
          justify-content: center;
        `;
      case 'left':
      default:
        return css`
          justify-content: flex-start;
        `;
    }
  }}
`;

// Button Label
const StyledButtonLabel = styled.span`
  display: flex;
  align-items: center;
  svg {
    margin-right: 10px;
    width: 30px;
    height: 30px;
  }
  > span {
    font-family: ${({ theme }) => theme.text.font.family};
    font-size: ${({ theme }) => theme.custom.helpers.pxToRem(14)};
    font-weight: 700;
    letter-spacing: 0.25px;
    line-height: ${({ theme }) => theme.custom.helpers.pxToRem(28)};
    height: ${({ theme }) => theme.custom.helpers.pxToRem(28)};
  }
`;

// PRIMARY BUTTON

const PrimaryButtonStyles = css`
  color: white;
  border-color: ${({ theme }) => theme.global.colors.brand};
  background-color: ${({ theme }) => theme.global.colors.brand};
`;

const PrimaryButtonHoverStyles = css`
  color: white;
  border-color: black;
  background-color: black;
`;

const PrimaryButtonDarkStyles = css`
  color: white;
  border-color: ${({ theme }) => theme.global.colors.brand};
  background-color: ${({ theme }) => theme.global.colors.brand};
`;

const PrimaryButtonDarkHoverStyles = css`
  color: black;
  border-color: white;
  background-color: white;
`;

const PrimaryButtonDisabledStyles = css`
  background-color: ${({ theme }) => theme.global.colors['dark-1']};
`;

// SECONDARY BUTTON
const SecondaryButtonStyles = css`
  background-color: transparent;
  border: solid 1px ${({ theme }) => theme.global.colors.brand};
  color: ${({ theme }) => theme.global.colors.brand};
`;
const SecondaryButtonHoverStyles = css`
  color: white;
  border-color: ${({ theme }) => theme.global.colors.brand};
  background-color: ${({ theme }) => theme.global.colors.brand};
`;
const SecondaryButtonDisabledStyles = css`
  color: ${({ theme }) => theme.global.colors['dark-1']};
  border-color: ${({ theme }) => theme.global.colors['dark-1']};
`;

// TEXT BUTTON
const PlainButtonStyles = css`
  padding: 0;
  height: 28px;
  width: auto;
  display: inline-block;

  background-color: transparent;
  color: ${({ theme }) => theme.global.colors.brand};
  ${StyledButtonLabel} > span {
    transition: border-color 0.2s ease;
    border-bottom: solid 2px ${({ theme }) => theme.global.colors.brand};
  }
`;

const PlainButtonHoverStyles = css`
  color: black;
  ${StyledButtonLabel} > span {
    border-bottom: solid 2px black;
  }
`;

const PlainButtonDisabledStyles = css`
  color: ${({ theme }) => theme.global.colors['dark-1']};
`;

const StyledButton = styled.button`
  /* never break contents of a button */
  white-space: nowrap;
  padding: 1rem;
  background-color: #ccc;

  /* Resets inherited from Grommet's defaults */
  display: inline-block;
  font: inherit;
  text-decoration: none;
  margin: 0;
  overflow: visible;
  text-transform: none;
  border: none;

  cursor: ${({ $disabled }) =>
    $disabled === true ? 'not-allowed' : 'pointer'};

  /* Layout */
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  padding-left: 24px;
  padding-right: 24px;

  transition: all 0.2s ease;

  @media ${({ theme }) => theme.custom.layouts.below_medium} {
    width: 100%;
  }

  /* Base Varity Styles */
  ${({ $variety, $onDark }) => {
    switch ($variety) {
      case 'plain':
        return PlainButtonStyles;
      case 'secondary':
        return SecondaryButtonStyles;
      case 'primary':
      default:
        return $onDark ? PrimaryButtonDarkStyles : PrimaryButtonStyles;
    }
  }};

  /* Hover */
  @media (hover: hover) {
    &:hover {
      ${({ $disabled, $inactive, $variety, $onDark }) => {
        if ($disabled || $inactive) return null;
        switch ($variety) {
          case 'plain':
            return PlainButtonHoverStyles;
          case 'secondary':
            return SecondaryButtonHoverStyles;
          case 'primary':
          default:
            return $onDark
              ? PrimaryButtonDarkHoverStyles
              : PrimaryButtonHoverStyles;
        }
      }}
    }
  }

  &:focus {
    outline: solid 2px ${({ theme }) => theme.global.colors['status-focus']};
  }

  /* Disabled */
  ${({ $disabled, $inactive, $variety }) => {
    if (!$disabled && !$inactive) return null;
    switch ($variety) {
      case 'plain':
        return PlainButtonDisabledStyles;
      case 'secondary':
        return SecondaryButtonDisabledStyles;
      case 'primary':
      default:
        return PrimaryButtonDisabledStyles;
    }
  }}
`;

const ButtonLabel = ({ label, icon }) => (
  <StyledButtonLabel>
    {icon && icon}
    <span>{label}</span>
  </StyledButtonLabel>
);
ButtonLabel.propTypes = {
  label: PropTypes.string.isRequired,
};
const Button = (props) => {
  const {
    label,
    href,
    type,
    onClick,
    disabled,
    inactive,
    onDark,
    variety,
    download,
    target,
    id,
    icon,
  } = props;
  /**
   * A button with super powers.
   *
   * We have to diverge from Grommet here as rendering it as a Gatsby link
   * for internal links produces several errors as it attempts to pass the
   * Grommet props down to the Gatsby Link.
   *
   * If the href attribute includes a '//' we assume it is an external link.
   * If the href attribute doesn't have a '//' we assume it's internal.
   * If there is not href, we assume it is a normal button.
   *
   */

  if (href && isExternalLink(href)) {
    // External link
    return (
      <StyledButton
        as="a"
        href={href}
        id={id}
        $inactive={inactive}
        $disabled={disabled}
        $variety={variety}
        $onDark={onDark}
        title={label}
        target={target || '_blank'}
        download={download}
      >
        <ButtonLabel label={label} icon={icon} />
      </StyledButton>
    );
  }
  if (href) {
    // Internal link
    return (
      <StyledButton
        as={GatsbyLink}
        to={href}
        id={id}
        $inactive={inactive}
        $disabled={disabled}
        $onDark={onDark}
        title={label}
        target={target}
        $variety={variety}
      >
        <ButtonLabel label={label} icon={icon} />
      </StyledButton>
    );
  }
  // Some sort of regular button
  return (
    <StyledButton
      $inactive={inactive}
      $disabled={disabled}
      title={label}
      $onDark={onDark}
      id={id}
      disabled={disabled}
      onClick={onClick}
      type={type}
      target={target}
      as="button"
      $variety={variety}
    >
      <ButtonLabel label={label} icon={icon} />
    </StyledButton>
  );
};

Button.defaultProps = {
  onDark: false,
  variety: 'primary',
  href: null,
  type: 'button',
  onClick: () => null,
  disabled: false,
  inactive: false,
  download: false,
  target: null,
  id: null,
  icon: null,
};

Button.propTypes = {
  onDark: PropTypes.bool,
  icon: PropTypes.node,
  variety: PropTypes.oneOf(['primary', 'secondary', 'plain']),
  label: PropTypes.string.isRequired,
  href: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit']),
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  inactive: PropTypes.bool,
  download: PropTypes.bool,
  target: PropTypes.string,
  id: PropTypes.string,
};

export { Button, ButtonContainer };
